<template>
	<!-- 20260218 -- Copilot (Claude Sonnet 4.6) -- Replaced :value on <select> with :selected on
	     each <option>. When the options list is updated reactively (e.g. loadSubscriptionProducts
	     resolves), Vue recreates the <option> elements AFTER applying :value to the <select>, which
	     causes the browser to reset select.value to the first option. Binding :selected on each
	     option applies the selection during option creation so it survives option list refreshes. -->
	<select
		:id="id"
		:class="selectClass"
		@input="handleInput"
		:disabled="disabled"
		:required="required"
		:aria-invalid="state === false ? 'true' : null"
	>
		<template v-for="(item, index) in normalizedOptions" :key="index">
			<!-- Grouped options (optgroup) -->
			<optgroup v-if="item.label" :label="item.label">
				<option
					v-for="(option, optIndex) in item.options"
					:key="optIndex"
					:value="option.value"
					:selected="option.value === modelValue"
					:disabled="option.disabled"
				>
					{{ option.text }}
				</option>
			</optgroup>
			
			<!-- Flat options -->
			<option
				v-else
				:value="item.value"
				:selected="item.value === modelValue"
				:disabled="item.disabled"
			>
				{{ item.text }}
			</option>
		</template>
	</select>
</template>

<script>
/**
 * BFormSelect Compatibility Wrapper
 * 
 * CREATED: Dec 2024 - Vue 3 Migration
 * UPDATED: Dec 2024 - Added optgroup support for grouped options
 * WHO: GitHub Copilot (Claude Sonnet 4.5)
 * WHY: BootstrapVue 2.23.1 incompatible with Vue 3; provides bridge component
 * 
 * PURPOSE:
 * - Drop-in replacement for BootstrapVue's b-form-select
 * - Mimics BootstrapVue API for minimal migration effort
 * - Supports both flat and grouped options (optgroups)
 * - Easy to swap with bootstrap-vue-next when ready
 * 
 * FEATURES:
 * - Flat options: [{text: 'Option 1', value: '1'}, ...] or ['Option 1', 'Option 2', ...]
 * - Grouped options: [{label: 'Group 1', options: [{text: 'Item', value: 'val'}]}, ...]
 * - BootstrapVue-compatible props: valueField, textField, disabledField
 * - Bootstrap 5 styling with validation states
 * - Size variants: sm, lg
 * 
 * MIGRATION PATH:
 * 1. Current: Using this wrapper with native <select>
 * 2. Future: Replace import with bootstrap-vue-next's BFormSelect
 * 3. Props are intentionally BootstrapVue-compatible
 */

export default {
	name: 'BFormSelect',
	props: {
		// Vue 3 uses modelValue instead of value
		modelValue: {
			type: [String, Number, Boolean, Object, Array],
			default: null
		},
		
		// BootstrapVue-compatible props
		options: {
			type: Array,
			default: () => []
		},
		valueField: {
			type: String,
			default: 'value'
		},
		textField: {
			type: String,
			default: 'text'
		},
		disabledField: {
			type: String,
			default: 'disabled'
		},
		
		// Styling props
		size: {
			type: String,
			default: null,
			validator: (value) => ['sm', 'lg', null].includes(value)
		},
		state: {
			type: Boolean,
			default: null
		},
		
		// Standard HTML attributes
		id: {
			type: String,
			default: null
		},
		disabled: {
			type: Boolean,
			default: false
		},
		required: {
			type: Boolean,
			default: false
		},
		validated: {
			type: Boolean,
			default: false
		}
	},
	
	computed: {
		selectClass() {
			const classes = ['form-control', 'form-select']
			
			// Size classes
			if (this.size === 'sm') classes.push('form-control-sm', 'form-select-sm')
			if (this.size === 'lg') classes.push('form-control-lg', 'form-select-lg')
			
			// Validation state classes
			if (this.state === true) classes.push('is-valid')
			if (this.state === false) classes.push('is-invalid')
			
			return classes.join(' ')
		},
		
		normalizedOptions() {
			if (!this.options || this.options.length === 0) {
				return []
			}
			
			return this.options.map(option => {
				// Handle grouped options (optgroup format: {label: 'Group', options: [...]})
				if (option.label && Array.isArray(option.options)) {
					return {
						label: option.label,
						options: option.options.map(opt => this.normalizeOption(opt))
					}
				}
				
				// Handle flat options
				return this.normalizeOption(option)
			})
		}
	},
	
	methods: {
		normalizeOption(option) {
			// Handle string options
			if (typeof option === 'string') {
				return {
					value: option,
					text: option,
					disabled: false
				}
			}
			
			// Handle object options
			if (typeof option === 'object') {
				return {
					value: option[this.valueField] ?? option.value ?? option.item,
					text: option[this.textField] ?? option.text ?? option.name ?? option.value,
					disabled: option[this.disabledField] ?? option.disabled ?? false
				}
			}
			
			// Fallback for other types
			return {
				value: option,
				text: String(option),
				disabled: false
			}
		},
		
		handleInput(event) {
			const value = event.target.value
			
			// Emit update:modelValue for v-model support
			this.$emit('update:modelValue', value)
			
			// Also emit input and change for compatibility
			this.$emit('input', value)
			this.$emit('change', value)
		}
	}
}
</script>

<style scoped>
/* Ensure Bootstrap form-select styling applies */
select.form-select,
select.form-control {
	display: block;
	width: 100%;
	padding: 0.375rem 2.25rem 0.375rem 0.75rem;
	font-size: 1rem;
	font-weight: 400;
	line-height: 1.5;
	color: #212529;
	background-color: #fff;
	background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e");
	background-repeat: no-repeat;
	background-position: right 0.75rem center;
	background-size: 16px 12px;
	border: 1px solid #ced4da;
	border-radius: 0.25rem;
	transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
	appearance: none;
}

select.form-select:focus,
select.form-control:focus {
	border-color: #86b7fe;
	outline: 0;
	box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

select.form-select.is-valid,
select.form-control.is-valid {
	border-color: #198754;
	padding-right: calc(0.75em + 2.3125rem);
	background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e"),
		url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 8'%3e%3cpath fill='%23198754' d='M2.3 6.73L.6 4.53c-.4-1.04.46-1.4 1.1-.8l1.1 1.4 3.4-3.8c.6-.63 1.6-.27 1.2.7l-4 4.6c-.43.5-.8.4-1.1.1z'/%3e%3c/svg%3e");
	background-position: right 0.75rem center, center right 2.25rem;
	background-size: 16px 12px, calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
}

select.form-select.is-invalid,
select.form-control.is-invalid {
	border-color: #dc3545;
	padding-right: calc(0.75em + 2.3125rem);
	background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e"),
		url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' width='12' height='12' fill='none' stroke='%23dc3545'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e");
	background-position: right 0.75rem center, center right 2.25rem;
	background-size: 16px 12px, calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
}

select.form-select-sm,
select.form-control-sm {
	padding: 0.25rem 2rem 0.25rem 0.5rem;
	font-size: 0.875rem;
	border-radius: 0.2rem;
}

select.form-select-lg,
select.form-control-lg {
	padding: 0.5rem 2.5rem 0.5rem 1rem;
	font-size: 1.25rem;
	border-radius: 0.3rem;
}
</style>
